"use client";

import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import Image from 'next/image';
import Link from 'next/link';
import { SubPromotion } from '@/services/dataTypes';
import { getBaseImageUrl, getPromotionHref } from '@/constants/hooks';

import 'swiper/css';
import 'swiper/css/navigation';
import { faArrowRight, FontAwesomeIcon } from '@/constants/icons';

interface Props {
  subPromotions: SubPromotion[];
  domain: string;
  promotionSlug: string;
}

const SubPromotionSlider = ({ subPromotions, domain, promotionSlug }: Props) => {
  if (!subPromotions || subPromotions.length === 0) return null;

  return (
    <div className="relative">
      <Swiper
        modules={[Navigation, Autoplay]}
        spaceBetween={16}
        slidesPerView={1}
        navigation={{
          nextEl: '.promo-next',
          prevEl: '.promo-prev',
        }}
        autoplay={{
          delay: 4000,
          disableOnInteraction: false,
        }}
        breakpoints={{
          640: { slidesPerView: 2, spaceBetween: 16 },
          1024: { slidesPerView: 3, spaceBetween: 16 },
          1280: { slidesPerView: 4, spaceBetween: 16 },
        }}
      >
        {subPromotions.map((item) => (
          <SwiperSlide key={item.unique_id}>
            <Link
              href={getPromotionHref(item, promotionSlug)}
              className="block"
            >
              <div className="bg-white rounded-xl overflow-hidden shadow-sm h-[200px] relative">
                {/* Image Background */}
                <div className="absolute inset-0 opacity-20">
                  <Image
                    src={getBaseImageUrl(domain, item.category_image, '')}
                    alt={item.category_name}
                    fill
                    unoptimized
                    className="object-cover"
                    sizes="300px"
                  />
                </div>
                
                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-center p-6">
                  <h4 className="text-lg font-bold text-[#111827] mb-2 line-clamp-2">
                    {item.category_name}
                  </h4>
                  {item.category_detail && (
                    <p className="text-sm text-[#4B5563] line-clamp-1 mb-4">
                      {item.category_detail}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#F97316]">
                    <span>Explore</span>
                    <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Navigation */}
      <button className="promo-prev absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center z-10 text-[#1E3A5F]">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button className="promo-next absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center z-10 text-[#1E3A5F]">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
};

export default SubPromotionSlider;
